import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Container, Typography, Box, Button, Grid, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, InputLabel, Select, CircularProgress, FormControlLabel, Checkbox, IconButton, InputAdornment, Tooltip, Snackbar, Alert as MuiAlert } from '@mui/material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import Axios from 'axios';
import StateContext from '../../Contexts/StateContext';
import { areaOptions, AranyosszekOptions, CsikszekOptions, GyergyoszekOptions, KaszonfiuszekOptions, KereszturfiuszekOptions, 
    KezdiszekOptions, MarosszekOptions, MiklosvarfiuszekOptions, OrbaiszekOptions, SepsiszekOptions, UdvarhelyszekOptions, CsangofoldOptions,
} from "../../Constants/GeographicConstants";
import config, { environment } from '../../config';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    shadowSize: [41, 41]
});

function LocationPicker({ position, setPosition, manualMode }) {
    const map = useMap();

    useEffect(() => {
        if (manualMode) {
            map.getContainer().style.cursor = 'crosshair';
        } else {
            map.getContainer().style.cursor = '';
        }
    }, [map, manualMode]);

    useEffect(() => {
        const handleClick = (e) => {
            setPosition(e.latlng);
        };
        map.on('click', handleClick);
        return () => {
            map.off('click', handleClick);
        };
    }, [map, setPosition]);

    useEffect(() => {
        if (position && !manualMode) {
            map.flyTo(position, 13);
        }
    }, [map, position, manualMode]);

    return position ? <Marker position={position} icon={defaultIcon} /> : null;
}

function SzeklerlandEvents() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const GlobalState = useContext(StateContext);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('fesztivál');
  const [newLocation, setNewLocation] = useState('');
  const [newSzek, setNewSzek] = useState('');
  const [newKozseg, setNewKozseg] = useState('');
  const [newTelepules, setNewTelepules] = useState('');
  const [markerPosition, setMarkerPosition] = useState(null);
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
  const [newEndDate, setNewEndDate] = useState('');
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [isCsango, setIsCsango] = useState(false);
  const [geocodingError, setGeocodingError] = useState('');
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [manualMapMode, setManualMapMode] = useState(false);

  // Private calendar & groups
  const [isPrivate, setIsPrivate] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Geographic options state
  const [szekelyfoldiKozsegek, setSzekelyfoldiKozsegek] = useState([]);
  const [szekelyfoldiTelepulesek, setSzekelyfoldiTelepulesek] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);

  // Csángó API options state
  const [csangoKozsegek, setCsangoKozsegek] = useState([]);
  const [csangoTelepulesek, setCsangoTelepulesek] = useState([]);
  const [csangoLoading, setCsangoLoading] = useState(false);

  const categories = useMemo(() => [
    { key: 'egyházi esemény', label: t('Events.Categories.Spiritual') },
    { key: 'fesztivál', label: t('Events.Categories.Festival') },
    { key: 'irodalom', label: t('Events.Categories.Literature') },
    { key: 'kiállítás megnyitó', label: t('Events.Categories.Exhibition') },
    { key: 'színház', label: t('Events.Categories.Theater') },
    { key: 'város- és falunapok', label: t('Events.Categories.TownDays') },
    { key: 'zene', label: t('Events.Categories.Music') },
  ].sort((a, b) => a.label.localeCompare(b.label)), [t]);

  const categoryLabels = useMemo(() => ({
    'zene': t('Events.Categories.Music'),
    'irodalom': t('Events.Categories.Literature'),
    'színház': t('Events.Categories.Theater'),
    'fesztivál': t('Events.Categories.Festival'),
    'egyházi esemény': t('Events.Categories.Spiritual'),
    'kiállítás megnyitó': t('Events.Categories.Exhibition'),
    'város- és falunapok': t('Events.Categories.TownDays')
  }), [t]);

  const categoryColors = useMemo(() => ({
    'zene': '#e91e63', // Pink
    'irodalom': '#9c27b0', // Purple
    'színház': '#673ab7', // Deep Purple
    'fesztivál': '#ff9800', // Orange
    'egyházi esemény': '#3f51b5', // Indigo
    'kiállítás megnyitó': '#4caf50', // Green
    'város- és falunapok': '#795548' // Brown
  }), []);

  const getSafeLanguage = useCallback(() => {
    const supportedLanguages = ['hu', 'en', 'ro', 'de', 'fr', 'es', 'it'];
    return supportedLanguages.includes(i18n.language) ? i18n.language : 'hu';
  }, [i18n.language]);

  const fetchEvents = useCallback(async () => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');
      const url = token 
        ? `${config[environment].szekelyfoldiEsemenyekEndpoint}?token=${token}`
        : config[environment].szekelyfoldiEsemenyekEndpoint;
        
      const axiosConfig = {};
      const jwtToken = GlobalState.jwtAccessToken || localStorage.getItem('token');
      if (jwtToken) {
        axiosConfig.headers = { Authorization: `Bearer ${jwtToken}` };
      }

      const response = await Axios.get(url, axiosConfig);
      setEvents(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching events:", error);
      setLoading(false);
    }
  }, [location.search, GlobalState.jwtAccessToken]);

  useEffect(() => {
    fetchEvents();
    if (location.state?.openAddDialog && GlobalState.userIsLogged) {
        setAddDialogOpen(true);
        // Clear state to avoid reopening on refresh
        navigate(location.pathname, { replace: true, state: {} });
    }
  }, [fetchEvents, location.state, GlobalState.userIsLogged, navigate, location.pathname]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchGeoData() {
      if (addDialogOpen) {
        setGeoLoading(true);
        try {
          if (isCsango) {
            // Fetch Csango data
            const fetchAllKozsegek = async (page = 1, accumulated = []) => {
              const response = await Axios.get(config[environment].csangoKozsegVarosEndpoint(page), { cancelToken: source.token });
              let data = [];
              if (response.data.features) {
                data = response.data.features.map(f => ({
                  id: f.properties.Id || f.properties.id || f.id,
                  nev: f.properties.név || f.properties.nev || f.properties.name || f.properties.nev_hu
                }));
              } else {
                const results = response.data.results || response.data || [];
                data = (Array.isArray(results) ? results : []).map(item => ({
                  id: item.id || item.uniqueid || item.pk,
                  nev: item.nev || item.name || item.nev_hu
                }));
              }
              const combined = [...accumulated, ...data];
              if (response.data.next) return fetchAllKozsegek(page + 1, combined);
              return combined;
            };

            const fetchAllTelepulesek = async (page = 1, accumulated = []) => {
              const response = await Axios.get(config[environment].csangoTelepulesEndpoint(page), { cancelToken: source.token });
              let data = [];
              if (response.data.features) {
                data = response.data.features.map(f => ({
                  id: f.properties.id || f.properties.Id || f.id,
                  nev_hu: f.properties.név_hu || f.properties.nev_hu,
                  nev_ro: f.properties.név_ro || f.properties.nev_ro,
                  parishId: f.properties.parishId || f.properties.parish_id || f.properties.parish || f.properties.parent_id
                }));
              } else {
                const results = response.data.results || response.data || [];
                data = (Array.isArray(results) ? results : []).map(item => ({
                  id: item.id || item.uniqueid || item.pk,
                  nev_hu: item.nev_hu || item.name_hu || item.nev || item.name,
                  nev_ro: item.nev_ro || item.name_ro || item.name,
                  parishId: item.parishId || item.parish_id || item.parish || item.parent_id
                }));
              }
              const combined = [...accumulated, ...data];
              if (response.data.next) return fetchAllTelepulesek(page + 1, combined);
              return combined;
            };

            const [kData, tData] = await Promise.all([fetchAllKozsegek(), fetchAllTelepulesek()]);
            const uniqueKozsegek = Array.from(new Map(kData.map(item => [item.id, item])).values())
              .sort((a, b) => (a.nev || '').localeCompare(b.nev || ''));
            setCsangoKozsegek(uniqueKozsegek);
            setCsangoTelepulesek(tData);
          } else if (newSzek) {
            // Fetch Szekelyfold data
            // Megkeressük a szék objektumot a név kinyeréséhez
            const selectedSzekObj = areaOptions.find(s => s.value === newSzek || s.label === newSzek);
            const szekNameOrId = selectedSzekObj ? (selectedSzekObj.value || selectedSzekObj.label) : newSzek;
            const paramName = selectedSzekObj ? 'szek_nev' : 'szek';

            const kozsegResponse = await Axios.get(`${config[environment].apiEndpoint}api/kozsegvaros-dropdown/?${paramName}=${encodeURIComponent(szekNameOrId)}`, { cancelToken: source.token });
            const kData = Array.isArray(kozsegResponse.data) ? kozsegResponse.data : (kozsegResponse.data.results || []);
            setSzekelyfoldiKozsegek(kData);

            if (newKozseg) {
                const selectedKozsegObj = kData.find(k => (k.nev === newKozseg || k.uniqueid === newKozseg || String(k.id) === String(newKozseg)));
                if (selectedKozsegObj && selectedKozsegObj.reghuid) {
                    const telepulesResponse = await Axios.get(`${config[environment].apiEndpoint}api/telepules-dropdown/?reghuid=${selectedKozsegObj.reghuid}`, { cancelToken: source.token });
                    const tData = Array.isArray(telepulesResponse.data) ? telepulesResponse.data : (telepulesResponse.data.results || []);
                    setSzekelyfoldiTelepulesek(tData);
                } else {
                    setSzekelyfoldiTelepulesek([]);
                }
            } else {
                setSzekelyfoldiTelepulesek([]);
            }
          }
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching geo data:", error);
        } finally {
          setGeoLoading(false);
        }
      }
    }
    fetchGeoData();
    return () => source.cancel();
  }, [isCsango, addDialogOpen, newSzek, newKozseg]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGeocode = async () => {
    if (!newLocation) return;
    setGeocodingLoading(true);
    setGeocodingError('');
    try {
      const response = await Axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(newLocation)}`);
      if (response.data && response.data.length > 0) {
        const { lat, lon } = response.data[0];
        setMarkerPosition({ lat: parseFloat(lat), lng: parseFloat(lon) });
        setManualMapMode(false);
      } else {
        setGeocodingError(t('Thematic.GeocodingNoResult', 'Nem található koordináta a megadott címhez.'));
      }
    } catch (e) {
      console.error("Geocoding error:", e);
      setGeocodingError(t('Thematic.GeocodingError', 'Hiba történt a keresés során.'));
    } finally {
      setGeocodingLoading(false);
    }
  };

  const getCategoryCount = (categoryKey) => {
    return events.filter(e => e.category === categoryKey).length;
  };

  const handleOpenAddDialog = (tabIndex = 0) => {
    setNewDate(selectedDate.toISOString().split('T')[0]);
    setIsPrivate(tabIndex === 1);
    setAddDialogOpen(true);
    setManualMapMode(false);
    setGeocodingError('');
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setNewTitle('');
    setNewDescription('');
    setNewCategory('fesztivál');
    setNewLocation('');
    setNewSzek('');
    setNewKozseg('');
    setNewTelepules('');
    setMarkerPosition(null);
    setNewEndDate('');
    setIsMultiDay(false);
    setIsCsango(false);
    setManualMapMode(false);
    setGeocodingError('');
    setIsPrivate(false);
    setGroupName('');
    setShareLink('');
    setShareDialogOpen(false);
  };

  const handleAddEvent = async () => {
    if (!newTitle || !newDate || !newCategory) return;
    try {
      const token = GlobalState.jwtAccessToken || localStorage.getItem('token');
      const response = await Axios.post(
        config[environment].createSzekelyfoldiEsemenyekEndpoint,
        {
          title: newTitle,
          description: newDescription,
          date: newDate,
          end_date: isMultiDay ? newEndDate : null,
          category: newCategory,
          location: newLocation,
          szek: newSzek,
          kozsegvaros: newKozseg,
          telepules: newTelepules,
          latitude: markerPosition ? markerPosition.lat : null,
          longitude: markerPosition ? markerPosition.lng : null,
          is_csango: isCsango,
          is_private: isPrivate,
          group_name: groupName
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (isPrivate && response.data.share_token) {
        const link = `${window.location.origin}${window.location.pathname}?token=${response.data.share_token}`;
        setShareLink(link);
        setSnackbar({ open: true, message: t('Events.PrivateEventCreated', 'Privát naptár sikeresen létrehozva!'), severity: 'success' });
        setShareDialogOpen(true);
        handleCloseAddDialog();
        fetchEvents();
      } else {
        setSnackbar({ open: true, message: t('Events.EventAdded', 'Esemény sikeresen hozzáadva!'), severity: 'success' });
        handleCloseAddDialog();
        fetchEvents();
      }
    } catch (error) {
      console.error("Error adding event:", error);
      if (error.response && error.response.data) {
        console.error("Server validation errors:", error.response.data);
      }
      setSnackbar({ open: true, message: t('Common.SomethingWentWrong', 'Valami hiba történt.'), severity: 'error' });
    }
  };

  const handleDeleteEvent = (eventId) => {
    setConfirmAction(() => async () => {
        try {
            const token = GlobalState.jwtAccessToken || localStorage.getItem('token');
            await Axios.delete(config[environment].deleteSzekelyfoldiEsemenyekEndpoint(eventId), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSnackbar({ open: true, message: t('Events.EventDeleted', 'Esemény törölve!'), severity: 'success' });
            fetchEvents();
        } catch (error) {
            console.error("Error deleting event:", error);
            setSnackbar({ open: true, message: t('Common.SomethingWentWrong', 'Valami hiba történt a törlés során.'), severity: 'error' });
        }
    });
    setConfirmDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: t('Common.Copied', 'Másolva a vágólapra!'), severity: 'success' });
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dateString = date.toISOString().split('T')[0];
      const dayEvents = events.filter(e => {
        if (e.end_date) {
          return dateString >= e.date && dateString <= e.end_date;
        }
        return e.date === dateString;
      });

      if (dayEvents.length > 0) {
        // Get unique category keys for this day
        const dayCategoryKeys = [...new Set(dayEvents.map(e => e.category))];
        
        return (
          <Box className="calendar-tile-content" sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '2px', mt: 0.5 }}>
            {dayCategoryKeys.map((catKey, index) => (
              <Box 
                key={index} 
                className="calendar-category-dot"
                sx={{ 
                  width: { xs: 4, md: 6 }, 
                  height: { xs: 4, md: 6 }, 
                  borderRadius: '50%', 
                  bgcolor: categoryColors[catKey] || 'primary.main',
                  display: 'inline-block'
                }} 
              />
            ))}
          </Box>
        );
      }
    }
    return null;
  };

  const getSzekOptions = () => isCsango ? [{ label: 'Csángóföld', value: 'csango' }] : areaOptions;
  const getKozsegOptions = () => {
    if (isCsango) {
      return csangoKozsegek.map(k => ({ label: k.nev, value: k.id }));
    }
    if (szekelyfoldiKozsegek.length > 0) {
        return szekelyfoldiKozsegek.map(k => ({ label: k.nev, value: k.uniqueid || k.id }));
    }
    switch (newSzek) {
        case 'Aranyosszék': return AranyosszekOptions;
        case 'Csíkszék': return CsikszekOptions;
        case 'Gyergyószék': return GyergyoszekOptions;
        case 'Kászonfiúszék': return KaszonfiuszekOptions;
        case 'Keresztúrfiúszék': return KereszturfiuszekOptions;
        case 'Kézdiszék': return KezdiszekOptions;
        case 'Marosszék': return MarosszekOptions;
        case 'Miklósvárfiúszék': return MiklosvarfiuszekOptions;
        case 'Orbaiszék': return OrbaiszekOptions;
        case 'Sepsiszék': return SepsiszekOptions;
        case 'Udvarhelyszék': return UdvarhelyszekOptions;
        case 'Csángóföld': return CsangofoldOptions;
        default: return [];
    }
  };

  const getTelepulesOptions = () => {
    if (isCsango) {
      const selectedKozsegObj = csangoKozsegek.find(k => String(k.id) === String(newKozseg));
      const selectedKozsegName = selectedKozsegObj ? selectedKozsegObj.nev : null;
      
      const filtered = csangoTelepulesek.filter(t => 
        (t.parishId && String(t.parishId) === String(newKozseg)) ||
        (selectedKozsegName && (t.nev_hu === selectedKozsegName || t.nev_ro === selectedKozsegName))
      );
      
      const unique = Array.from(new Map(filtered.map(item => [item.id, item])).values())
        .sort((a, b) => (a.nev_hu || '').localeCompare(b.nev_hu || ''));
        
      return unique.map(t => ({ label: t.nev_hu || t.nev_ro, value: t.id }));
    }
    if (szekelyfoldiTelepulesek.length > 0) {
        return szekelyfoldiTelepulesek.map(t => ({ label: t.nev, value: t.uniqueid || t.id }));
    }
    return [];
  };

  const selectedDateString = selectedDate.toISOString().split('T')[0];
  const filteredEvents = events.filter(e => {
    if (e.end_date) {
      return selectedDateString >= e.date && selectedDateString <= e.end_date;
    }
    return e.date === selectedDateString;
  });

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, marginTop: '50px'}}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/reklam')}
            sx={{ textTransform: 'none' }}
          >
            {t('Common.Back')}
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'var(--sznodarkblue)' }}>
            {t('Events.SzeklerlandEvents')}
          </Typography>
        </Box>
        {GlobalState.userIsLogged ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => handleOpenAddDialog(0)}
              sx={{ textTransform: 'none' }}
            >
              {t('Events.AddEvent', 'Esemény hozzáadása')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LockIcon />}
              onClick={() => handleOpenAddDialog(1)}
              sx={{ textTransform: 'none', bgcolor: 'var(--sznodarkblue)' }}
            >
              {t('Events.CreatePrivateCalendar', 'Privát naptár')}
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ textTransform: 'none' }}
          >
            {t('Header.Login', 'Bejelentkezés')}
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {categories.map((cat) => (
          <Button
            key={cat.key}
            variant="outlined"
            size="small"
            startIcon={
              <Box 
                sx={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  bgcolor: categoryColors[cat.key] || 'primary.main',
                  mr: 0.5
                }} 
              />
            }
            onClick={() => scrollToSection(`category-${cat.key.replace(/\s+/g, '-')}`)}
            sx={{ 
              textTransform: 'none', 
              borderRadius: '20px',
              borderColor: 'var(--sznolightblue)',
              color: 'var(--sznodarkblue)',
              '&:hover': {
                borderColor: 'var(--sznodarkblue)',
                bgcolor: 'rgba(16, 91, 121, 0.04)'
              }
            }}
          >
            {cat.label} ({getCategoryCount(cat.key)})
          </Button>
        ))}
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              locale={getSafeLanguage()}
              tileContent={tileContent}
              className="custom-calendar"
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            {selectedDate.toLocaleDateString(getSafeLanguage(), { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
          {filteredEvents.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              {t('Events.NoEventsForDay', 'Erre a napra nincsenek események.')}
            </Typography>
          ) : (
            filteredEvents.map(event => (
              <Paper key={event.id} elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: categoryColors[event.category] || 'primary.main', position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {event.is_private && <LockIcon fontSize="small" color="action" />}
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{event.title}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {event.is_private && (
                      <Tooltip title={t('Events.CopyShareLink', 'Megosztási link másolása')}>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = `${window.location.origin}${window.location.pathname}?token=${event.share_token}`;
                            navigator.clipboard.writeText(link);
                            setSnackbar({ open: true, message: t('Events.LinkCopied', 'Link a vágólapra másolva!'), severity: 'success' });
                          }}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {GlobalState.userIsLogged && event.user_username === GlobalState.userName && (
                      <IconButton size="small" color="error" onClick={() => handleDeleteEvent(event.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'primary.dark', fontWeight: 'bold' }}>
                  {(categoryLabels[event.category] || event.category).toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  📅 {event.date} {event.end_date ? `- ${event.end_date}` : ''}
                </Typography>
                {event.location && (
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    📍 {event.location}
                  </Typography>
                )}
                <Typography variant="body2">{event.description}</Typography>
                {event.group_name && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, p: 0.5, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, width: 'fit-content', fontWeight: 'bold' }}>
                    📁 {event.group_name}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('Events.PostedBy', 'Beküldte')}: {event.user_username}
                </Typography>
              </Paper>
            ))
          )}
        </Grid>
      </Grid>

      <Box sx={{ mt: 6 }}>
        {categories.map((cat) => {
          const catEvents = events.filter(e => e.category === cat.key);
          if (catEvents.length === 0) return null;
          return (
            <Box key={cat.key} id={`category-${cat.key.replace(/\s+/g, '-')}`} sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: 'var(--sznodarkblue)', borderBottom: '2px solid var(--sznolightblue)', pb: 0.5 }}>
                {cat.label.toUpperCase()} ({catEvents.length})
              </Typography>
              <Grid container spacing={2}>
                {catEvents.map(event => (
                  <Grid item xs={12} sm={6} md={4} key={event.id}>
                    <Paper elevation={2} sx={{ p: 2, height: '100%', borderLeft: '4px solid', borderColor: categoryColors[event.category] || 'primary.main', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {event.is_private && <LockIcon fontSize="small" color="action" />}
                          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{event.title}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {event.is_private && (
                            <Tooltip title={t('Events.CopyShareLink', 'Megosztási link másolása')}>
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const link = `${window.location.origin}${window.location.pathname}?token=${event.share_token}`;
                                  navigator.clipboard.writeText(link);
                                  setSnackbar({ open: true, message: t('Events.LinkCopied', 'Link a vágólapra másolva!'), severity: 'success' });
                                }}
                              >
                                <ShareIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {GlobalState.userIsLogged && event.user_username === GlobalState.userName && (
                            <IconButton size="small" color="error" onClick={() => handleDeleteEvent(event.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}>
                        📅 {event.date} {event.end_date ? `- ${event.end_date}` : ''}
                      </Typography>
                      {event.location && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          📍 {event.location}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>{event.description}</Typography>
                      {event.group_name && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, p: 0.5, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, width: 'fit-content', fontWeight: 'bold' }}>
                          📁 {event.group_name}
                        </Typography>
                      )}
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {event.user_username}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {event.latitude && event.longitude && (
                            <Button size="small" onClick={() => {
                              setSelectedDate(new Date(event.date));
                              scrollToSection('event-map');
                            }}>
                              {t('Events.ShowOnMap', 'Térképen')}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ mb: 4, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon /> {t('Events.Timeline', 'Idővonal')}
        </Typography>
        <Box sx={{ position: 'relative', ml: { xs: 2, md: 4 }, borderLeft: '2px solid var(--sznolightblue)', pl: { xs: 3, md: 5 }, pb: 4 }}>
          {events.sort((a, b) => new Date(a.date) - new Date(b.date)).map((event, index) => (
            <Box key={event.id} sx={{ position: 'relative', mb: 4 }}>
              <Box sx={{ 
                position: 'absolute', 
                left: { xs: -33, md: -49 }, 
                top: 0, 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                bgcolor: categoryColors[event.category] || 'primary.main',
                border: '4px solid white',
                boxShadow: '0 0 0 2px var(--sznolightblue)'
              }} />
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                {event.date} {event.end_date ? `- ${event.end_date}` : ''}
              </Typography>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid', borderColor: categoryColors[event.category] || 'primary.main', maxWidth: '600px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {event.is_private && <LockIcon fontSize="small" color="action" />}
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{event.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{event.description}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {event.location && (
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      📍 {event.location}
                    </Typography>
                  )}
                  {event.group_name && (
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}>
                      📁 {event.group_name}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: 6 }} id="event-map">
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('Events.EventMap', 'Események térképen')}
        </Typography>
        <Paper elevation={3} sx={{ height: 400, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <MapContainer center={[45.8643, 25.7848]} zoom={8} style={{ height: '100%', width: '100%' }}>
            <TileLayer url={config[environment].osmTiles} />
            {events.filter(e => e.latitude && e.longitude).map(event => (
              <Marker key={event.id} position={[event.latitude, event.longitude]} icon={defaultIcon}>
                <Popup>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{event.title}</Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>{event.date}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>{event.location}</Typography>
                  <Button size="small" variant="text" onClick={() => setSelectedDate(new Date(event.date))}>
                    {t('Events.ShowDetails', 'Részletek')}
                  </Button>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Paper>
      </Box>

      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {!isPrivate ? t('Events.AddPublicEvent', 'Publikus esemény hozzáadása') : t('Events.AddPrivateEvent', 'Privát naptár bejegyzés')}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label={t('Events.EventTitle', 'Esemény megnevezése')}
            fullWidth
            variant="outlined"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <FormControlLabel
                control={
                <Checkbox
                    checked={isMultiDay}
                    onChange={(e) => setIsMultiDay(e.target.checked)}
                    color="primary"
                />
                }
                label={t('Events.MultiDay', 'Többnapos esemény')}
            />
            <FormControlLabel
                control={
                <Checkbox
                    checked={isCsango}
                    onChange={(e) => {
                        setIsCsango(e.target.checked);
                        setNewSzek(e.target.checked ? 'csango' : '');
                        setNewKozseg('');
                        setNewTelepules('');
                    }}
                    color="primary"
                />
                }
                label={t('Thematic.IsCsango', 'Csángó tartalom')}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label={t('Events.Date', 'Dátum')}
              type="date"
              fullWidth
              variant="outlined"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {isMultiDay && (
              <TextField
                margin="dense"
                label={t('Events.EndDate', 'Befejező dátum')}
                type="date"
                fullWidth
                variant="outlined"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>{t('Events.Category')}</InputLabel>
            <Select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              label={t('Events.Category')}
            >
              {categories.map(cat => (
                <MenuItem key={cat.key} value={cat.key}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label={t('Events.Location', 'Helyszín')}
            fullWidth
            variant="outlined"
            value={newLocation}
            onChange={(e) => {
              setNewLocation(e.target.value);
              setGeocodingError('');
            }}
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {geocodingLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Tooltip title={t('Thematic.Geocode', 'Koordináták keresése')}>
                      <IconButton onClick={handleGeocode} edge="end">
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
          />
          {geocodingError && (
            <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
              {geocodingError}
            </Typography>
          )}

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {t('Events.LocationDetails', 'Település választása vagy manuális kijelölés a térképen')}
            </Typography>
            <Button
                size="small"
                variant={manualMapMode ? "contained" : "outlined"}
                color="secondary"
                startIcon={<MyLocationIcon />}
                onClick={() => setManualMapMode(!manualMapMode)}
                sx={{ textTransform: 'none' }}
            >
                {manualMapMode ? t('Events.ManualModeOn', 'Kijelölés a térképen...') : t('Events.ManualModeOff', 'Kézi kijelölés')}
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>{t('Events.Szek', 'Szék')}</InputLabel>
                <Select
                  value={newSzek}
                  onChange={(e) => { setNewSzek(e.target.value); setNewKozseg(''); setNewTelepules(''); }}
                  label={t('Events.Szek', 'Szék')}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {getSzekOptions().map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>{t('Events.Kozseg', 'Község')}</InputLabel>
                <Select
                  value={newKozseg}
                  onChange={(e) => { setNewKozseg(e.target.value); setNewTelepules(''); }}
                  label={t('Events.Kozseg', 'Község')}
                  disabled={!newSzek || geoLoading}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {geoLoading && isCsango ? <MenuItem disabled><CircularProgress size={20} /></MenuItem> : getKozsegOptions().map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>{t('Events.Telepules', 'Település')}</InputLabel>
                <Select
                  value={newTelepules}
                  onChange={(e) => setNewTelepules(e.target.value)}
                  label={t('Events.Telepules', 'Település')}
                  disabled={!newKozseg || (geoLoading && !isCsango)}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {geoLoading && !isCsango ? (
                    <MenuItem disabled><CircularProgress size={20} /></MenuItem>
                  ) : (
                    getTelepulesOptions().length > 0 ? (
                      getTelepulesOptions().map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))
                    ) : (
                      !isCsango && (
                        <Box sx={{ p: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={t('Events.Telepules', 'Település')}
                            value={newTelepules}
                            onChange={(e) => setNewTelepules(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Box>
                      )
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('Events.PinLocation', 'Helyszín kitűzése (opcionális)')}</Typography>
          <Box sx={{ height: 250, width: '100%', mb: 2, borderRadius: 1, overflow: 'hidden', border: '1px solid #ccc' }}>
            <MapContainer center={[45.8643, 25.7848]} zoom={8} style={{ height: '100%', width: '100%' }}>
              <TileLayer url={config[environment].osmTiles} />
              <LocationPicker position={markerPosition} setPosition={setMarkerPosition} manualMode={manualMapMode} />
            </MapContainer>
          </Box>

          <TextField
            margin="dense"
            label={t('Events.Description', 'Leírás')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            sx={{ mb: 2 }}
          />

          {isPrivate && (
            <TextField
              margin="dense"
              label={t('Events.GroupName', 'Csoport megnevezése (pl. Család nyári programjai)')}
              fullWidth
              variant="outlined"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Pl. Kovács család nyaralása"
            />
          )}

          {shareLink && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1, border: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                <CheckCircleOutlineIcon fontSize="small" /> {t('Events.PrivateCalendarCreated', 'A privát naptár elkészült!')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
                {t('Events.ShareLinkDescription', 'Ezzel a linkkel oszthatja meg a naptárat másokkal. Csak ők fogják látni ezeket az eseményeket.')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField 
                  fullWidth 
                  size="small" 
                  value={shareLink} 
                  InputProps={{ readOnly: true }} 
                />
                <IconButton color="primary" onClick={() => copyToClipboard(shareLink)}>
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAddDialog} color="inherit">{t('Common.Cancel', 'Mégse')}</Button>
          <Button onClick={handleAddEvent} variant="contained" color={!isPrivate ? "success" : "primary"}>
            {!isPrivate ? t('Events.Publish', 'Közzététel') : t('Events.SaveToCalendar', 'Mentés a naptárba')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>{t('Common.Confirm', 'Megerősítés')}</DialogTitle>
        <DialogContent>
          <Typography>{t('Events.ConfirmDelete', 'Biztosan törölni szeretné ezt az eseményt?')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>{t('Common.Cancel', 'Mégse')}</Button>
          <Button 
            onClick={() => {
              if (confirmAction) confirmAction();
              setConfirmDialogOpen(false);
            }} 
            color="error" 
            variant="contained"
          >
            {t('Common.Delete', 'Törlés')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Share Link Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
          <CheckCircleOutlineIcon /> {t('Events.PrivateCalendarCreated', 'A privát naptár elkészült!')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('Events.ShareLinkDescription', 'Ezzel a linkkel oszthatja meg a naptárat másokkal. Csak ők fogják látni ezeket az eseményeket.')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
            <TextField 
              fullWidth 
              variant="outlined"
              size="small" 
              value={shareLink} 
              InputProps={{ 
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <ShareIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }} 
            />
            <Tooltip title={t('Common.Copy', 'Másolás')}>
              <IconButton color="primary" onClick={() => copyToClipboard(shareLink)} sx={{ bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
            {t('Events.ShareLinkNote', 'Mentse el ezt a linket, mert később nem tudjuk újra megjeleníteni.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)} variant="contained" color="primary">
            {t('Common.Close', 'Bezárás')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SzeklerlandEvents;